import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import type { ReimportSummary } from '../../types/reimportTypes';

interface ReimportSummaryTilesProps {
  summary: ReimportSummary;
}

const formatMoney = (value: number): string => {
  return `£${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ReimportSummaryTiles: React.FC<ReimportSummaryTilesProps> = ({
  summary,
}) => {
  const allTiles = [
    {
      value: summary.plotsAdded,
      display: `+${summary.plotsAdded}`,
      label: 'plots added',
      color: 'success.main',
    },
    {
      value: summary.plotsRemoved,
      display: `-${summary.plotsRemoved}`,
      label: 'plots removed',
      color: 'error.main',
    },
    {
      value: summary.labourItemsAdded,
      display: `+${summary.labourItemsAdded}`,
      label: 'items added',
      color: 'success.main',
    },
    {
      value: summary.labourItemsRemoved,
      display: `-${summary.labourItemsRemoved}`,
      label: 'items removed',
      color: 'error.main',
    },
    {
      value: summary.labourItemsPriceChanged,
      display: `~${summary.labourItemsPriceChanged}`,
      label: 'prices changed',
      color: 'warning.main',
    },
    {
      value: summary.moneyAdded,
      display: `+${formatMoney(summary.moneyAdded)}`,
      label: 'value increase',
      color: 'success.main',
    },
    {
      value: summary.moneyRemoved,
      display: `-${formatMoney(summary.moneyRemoved)}`,
      label: 'value decrease',
      color: 'error.main',
    },
  ];

  const visibleTiles = allTiles.filter(tile => tile.value > 0);

  if (visibleTiles.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="reimport-summary-tiles"
      >
        No changes detected
      </Typography>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}
      data-testid="reimport-summary-tiles"
    >
      {visibleTiles.map((tile, index) => (
        <Card key={index} sx={{ flex: '0 1 auto', minWidth: 100 }}>
          <CardContent
            sx={{
              textAlign: 'center',
              py: 1.5,
              px: 2,
              '&:last-child': { pb: 1.5 },
            }}
          >
            <Typography variant="h5" color={tile.color} gutterBottom>
              {tile.display}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {tile.label}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default ReimportSummaryTiles;
